import React from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useQueryParams } from '../utils/queryParams';

import environments from '../environments';
import { BASENAME, STATIC_DPP_TOP_URL } from '../constants';
import { getAgeFromBirthday } from '../utils';
import { usePrice } from '../utils/price';
import { getSelectedParts } from '../utils/localStorage';
import { sessionKey } from '../modules/app';

const searchLinkText = '検索結果';
const searchLinkUrl = `${STATIC_DPP_TOP_URL}${BASENAME}/search`;
const searchaLinkUrl = `${STATIC_DPP_TOP_URL}${BASENAME}/searcha`;
const planListText = '施設プラン一覧';
const planListUrl = `${STATIC_DPP_TOP_URL}${BASENAME}/plan_list`;
const planDetailText = 'プラン詳細';
const planDetailUrl = `${STATIC_DPP_TOP_URL}${BASENAME}/plan_detail`;


const Head = () => {
  const pcView = window.innerWidth > 768;
  const location = useLocation();
  const { pathname } = location;
  const query = useQueryParams();

  // Partsパラメータの値によってヘッダーとフッターを切り替える
  const defaultHeaderJS = '//www.nta.co.jp/search/headerfooter/dpp/header.js';
  const defaultFooterJS = '//www.nta.co.jp/search/headerfooter/dpp/footer.js';
  const tbwHeaderJS = '//www.nta.co.jp/search/headerfooter/dpp/header_tbw.js';
  const tbwFooterJS = '//www.nta.co.jp/search/headerfooter/dpp/footer_tbw.js';
  const wstHeaderJS = '//www.nta.co.jp/search/headerfooter/dpp/header_wst.js';
  const wstFooterJS = '//www.nta.co.jp/search/headerfooter/dpp/footer_wst.js';
  const loggedInHeaderJS = process.env.NODE_ENV === 'prod'
    ? 'https://www.nta.co.jp/search/headerfooter/dpp/rsv/header.js'
    : 'https://ecwww9.nta.co.jp/search/headerfooter/dpp/rsv/header.js';
  const stageAerosmithJS = process.env.NODE_ENV === 'prod'
    ? 'https://travel.fraudprevention.jp/js/aerosmith.min.js?accountId=9c7dc5831592950d4286d90c11b6bacb&pageId=page1'
    : 'https://stage-travel.fraudprevention.jp/js/stage-aerosmith.min.js?accountId=29e04e6cac922b266ed75d4e33b19f54&pageId=page1&verbose=true';

  const normalizeParts = (value) => {
    if (value == null) return undefined;
    const str = String(value);
    if (!str || str === 'undefined' || str === 'null') return undefined;
    if (str !== 'tbw' && str !== 'wst') return undefined;
    return str;
  };

  // sessionKey は QueryParams でセットされる（初回レンダーでは未セットの可能性があるため URL の k もフォールバック）
  const key = sessionKey || query.k;
  const partsFromStorage = key ? getSelectedParts(key) : undefined;
  const partsFromQuery = normalizeParts(query.Parts);
  const parts = partsFromStorage || partsFromQuery || null;

  const headerJS = parts === 'tbw' ? tbwHeaderJS : (parts === 'wst' ? wstHeaderJS : defaultHeaderJS);
  const footerJS = parts === 'tbw' ? tbwFooterJS : (parts === 'wst' ? wstFooterJS : defaultFooterJS);

  switch (pathname) {

  case '/search': {
    return (
      <Helmet>
        <title>航空券+ホテルパック | 日本旅行</title>
        <meta name="description" content="航空券+ホテルパックの検索結果 | 国内航空券とホテルがセットになったお得なダイナミックパッケージ。国内旅行・ツアーの予約は日本旅行" />
        <meta name="keywords" content="飛行機,ホテル,パック,セット,航空券,ツアー,旅行,日本旅行" />
        <link rel="canonical" href={`${STATIC_DPP_TOP_URL}${BASENAME}${pathname}`} />
        <script type="text/javascript" src={headerJS} charset="utf-8"></script>
        <script type="text/javascript" src={footerJS} charset="utf-8"></script>
        <script type="text/javascript">{`!function(r,t,j,s){(j=r.createElement(t)).type="text/javascript",j.async=!0,j.charset="utf-8",j.src="//js.rtoaster.jp/${environments.RTOASTER_CODE}/rt.js",(s=r.getElementsByTagName(t)[0]).parentNode.insertBefore(j,s)}(document,"script")`}</script>
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement":
            [
            {"@type": "ListItem","position": 1,"item": {"@id": "https://www.nta.co.jp/","name": "日本旅行トップ"}}
            ,{"@type": "ListItem","position": 2,"item": {"@id": "https://www.nta.co.jp/kokunai/","name": "国内旅行・国内ツアー"}}
            ,{"@type": "ListItem","position": 3,"item": {"@id": "https://www.nta.co.jp/kokunai/special/air/","name": "航空+宿泊セットプラン"}}
            ,{"@type": "ListItem","position": 4,"item": {"@id": "${searchLinkUrl}","name": "${searchLinkText}"}}
            ]
          }
        `}</script>
      </Helmet>
    );
  }

  case '/searcha': {
    return (
      <Helmet>
        <title>航空券+ホテルパック | 日本旅行</title>
        <meta name="description" content="航空券+ホテルパックの検索結果 | 国内航空券とホテルがセットになったお得なダイナミックパッケージ。国内旅行・ツアーの予約は日本旅行" />
        <meta name="keywords" content="飛行機,ホテル,パック,セット,航空券,ツアー,旅行,日本旅行" />
        <link rel="canonical" href={`${STATIC_DPP_TOP_URL}${BASENAME}${pathname}`} />
        <script type="text/javascript" src={headerJS} charset="utf-8"></script>
        <script type="text/javascript" src={footerJS} charset="utf-8"></script>
        <script type="text/javascript">{`!function(r,t,j,s){(j=r.createElement(t)).type="text/javascript",j.async=!0,j.charset="utf-8",j.src="//js.rtoaster.jp/${environments.RTOASTER_CODE}/rt.js",(s=r.getElementsByTagName(t)[0]).parentNode.insertBefore(j,s)}(document,"script")`}</script>
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement":
            [
            {"@type": "ListItem","position": 1,"item": {"@id": "https://www.nta.co.jp/","name": "日本旅行トップ"}}
            ,{"@type": "ListItem","position": 2,"item": {"@id": "https://www.nta.co.jp/kokunai/","name": "国内旅行・国内ツアー"}}
            ,{"@type": "ListItem","position": 3,"item": {"@id": "https://www.nta.co.jp/kokunai/special/air/","name": "航空+宿泊セットプラン"}}
            ,{"@type": "ListItem","position": 4,"item": {"@id": "${searchLinkUrl}","name": "${searchLinkText}"}}
            ,{"@type": "ListItem","position": 5,"item": {"@id": "${searchaLinkUrl}","name": "${planListText}"}}
            ]
          }
        `}</script>
      </Helmet>
    );
  }

  case '/plan_list': {
    return (
      <Helmet>
        <title>施設プラン一覧 | 日本旅行</title>
        <meta name="description" content="航空券+ホテルパックのプラン一覧 | 国内航空券とホテルがセットになったお得なダイナミックパッケージ。国内旅行・ツアーの予約は日本旅行" />
        <meta name="keywords" content="プラン一覧,飛行機,ホテル,パック,セット,航空券,ツアー,旅行,日本旅行" />
        <script type="text/javascript" src={headerJS} charset="utf-8"></script>
        <script type="text/javascript" src={footerJS} charset="utf-8"></script>
        <script type="text/javascript">{`!function(r,t,j,s){(j=r.createElement(t)).type="text/javascript",j.async=!0,j.charset="utf-8",j.src="//js.rtoaster.jp/${environments.RTOASTER_CODE}/rt.js",(s=r.getElementsByTagName(t)[0]).parentNode.insertBefore(j,s)}(document,"script")`}</script>
        <link rel="canonical" href={`${STATIC_DPP_TOP_URL}${BASENAME}${pathname}`} />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement":
            [
            {"@type": "ListItem","position": 1,"item": {"@id": "https://www.nta.co.jp/","name": "日本旅行トップ"}}
            ,{"@type": "ListItem","position": 2,"item": {"@id": "https://www.nta.co.jp/kokunai/","name": "国内旅行・国内ツアー"}}
            ,{"@type": "ListItem","position": 3,"item": {"@id": "https://www.nta.co.jp/kokunai/special/air/","name": "航空+宿泊セットプラン"}}
            ,{"@type": "ListItem","position": 4,"item": {"@id": "${searchLinkUrl}","name": "${searchLinkText}"}}
            ,{"@type": "ListItem","position": 5,"item": {"@id": "${planListUrl}","name": "${planListText}"}}
            ]
          }
        `}</script>
      </Helmet>
    );
  }

  case '/plan_detail': {
    return (
      <Helmet>
        <title>プラン詳細 | 日本旅行</title>
        <meta name="description" content="航空券+ホテルパックのプラン詳細 | 国内航空券とホテルがセットになったお得なダイナミックパッケージ。国内旅行・ツアーの予約は日本旅行" />
        <meta name="keywords" content="プラン詳細,飛行機,ホテル,パック,セット,航空券,ツアー,旅行,日本旅行" />
        <link rel="canonical" href={`${STATIC_DPP_TOP_URL}${BASENAME}${pathname}`} />
        <script type="text/javascript" src={headerJS} charset="utf-8"></script>
        <script type="text/javascript" src={footerJS} charset="utf-8"></script>
        <script type="text/javascript">{`!function(r,t,j,s){(j=r.createElement(t)).type="text/javascript",j.async=!0,j.charset="utf-8",j.src="//js.rtoaster.jp/${environments.RTOASTER_CODE}/rt.js",(s=r.getElementsByTagName(t)[0]).parentNode.insertBefore(j,s)}(document,"script")`}</script>
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement":
            [
            {"@type": "ListItem","position": 1,"item": {"@id": "https://www.nta.co.jp/","name": "日本旅行トップ"}}
            ,{"@type": "ListItem","position": 2,"item": {"@id": "https://www.nta.co.jp/kokunai/","name": "国内旅行・国内ツアー"}}
            ,{"@type": "ListItem","position": 3,"item": {"@id": "https://www.nta.co.jp/kokunai/special/air/","name": "航空+宿泊セットプラン"}}
            ,{"@type": "ListItem","position": 4,"item": {"@id": "${searchLinkUrl}","name": "${searchLinkText}"}}
            ,{"@type": "ListItem","position": 5,"item": {"@id": "${planListUrl}","name": "${planListText}"}}
            ,{"@type": "ListItem","position": 6,"item": {"@id": "${planDetailUrl}","name": "${planDetailText}"}}
            ]
          }
        `}</script>
      </Helmet>
    );
  }

  case '/entry_confirm': {
    return (
      <Helmet>
        <title>航空+宿泊セットプラン お申込み内容確認 | 日本旅行</title>
        <link rel="canonical" href={`${STATIC_DPP_TOP_URL}${BASENAME}${pathname}`} />
        <script type="text/javascript" src={headerJS} charset="utf-8"></script>
        <script type="text/javascript" src={footerJS} charset="utf-8"></script>
        <script type="text/javascript">{`!function(r,t,j,s){(j=r.createElement(t)).type="text/javascript",j.async=!0,j.charset="utf-8",j.src="//js.rtoaster.jp/${environments.RTOASTER_CODE}/rt.js",(s=r.getElementsByTagName(t)[0]).parentNode.insertBefore(j,s)}(document,"script")`}</script>
      </Helmet>
    );
  }

  case '/login': {
    const headerJS = loggedInHeaderJS;
    const footerJS = defaultFooterJS;
    return (
      <Helmet>
        <title>航空+宿泊セットプラン ログイン | 日本旅行</title>
        <link rel="canonical" href={`${STATIC_DPP_TOP_URL}${BASENAME}${pathname}`} />
        <script type="text/javascript" src={headerJS} charset="utf-8"></script>
        <script type="text/javascript" src={footerJS} charset="utf-8"></script>
        <script type="text/javascript">{`!function(r,t,j,s){(j=r.createElement(t)).type="text/javascript",j.async=!0,j.charset="utf-8",j.src="//js.rtoaster.jp/${environments.RTOASTER_CODE}/rt.js",(s=r.getElementsByTagName(t)[0]).parentNode.insertBefore(j,s)}(document,"script")`}</script>
      </Helmet>
    );
  }

  case '/plan_option': {
    return (
      <Helmet>
        <meta name="description" content="航空+宿泊セットプラン | 国内航空券と宿泊がセットになったお得なダイナミックパッケージ。国内旅行・ツアーの予約は日本旅行" />
        <meta name="keywords" content="国内航空券,飛行機,ダイナミックパッケージ,宿泊,ホテル,旅館,国内旅行,日本旅行" />
        <link rel="canonical" href={`${STATIC_DPP_TOP_URL}${BASENAME}${pathname}`} />
        <script type="text/javascript" src={headerJS} charset="utf-8"></script>
        <script type="text/javascript" src={footerJS} charset="utf-8"></script>
        <script type="text/javascript">{`!function(r,t,j,s){(j=r.createElement(t)).type="text/javascript",j.async=!0,j.charset="utf-8",j.src="//js.rtoaster.jp/${environments.RTOASTER_CODE}/rt.js",(s=r.getElementsByTagName(t)[0]).parentNode.insertBefore(j,s)}(document,"script")`}</script>
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement":
            [
            {"@type": "ListItem","position": 1,"item": {"@id": "https://www.nta.co.jp/","name": "日本旅行トップ"}}
            ,{"@type": "ListItem","position": 2,"item": {"@id": "https://www.nta.co.jp/kokunai/","name": "国内旅行・国内ツアー"}}
            ,{"@type": "ListItem","position": 3,"item": {"@id": "https://www.nta.co.jp/kokunai/special/air/","name": "航空+宿泊セットプラン"}}
            ,{"@type": "ListItem","position": 4,"item": {"@id": "${searchLinkUrl}","name": "${searchLinkText}"}}
            ,{"@type": "ListItem","position": 5,"item": {"@id": "${planListUrl}","name": "${planListText}"}}
            ,{"@type": "ListItem","position": 6,"item": {"@id": "${planDetailUrl}","name": "${planDetailText}"}}
            ]
          }
        `}</script>
      </Helmet>
    );
  }

  case '/complete': {
    const headerJS = loggedInHeaderJS;
    const footerJS = defaultFooterJS;
    return (
      <Helmet>
        <script type="text/javascript" src={headerJS} charset="utf-8"></script>
        <script type="text/javascript" src={footerJS} charset="utf-8"></script>
        <script type="text/javascript">{`!function(r,t,j,s){(j=r.createElement(t)).type="text/javascript",j.async=!0,j.charset="utf-8",j.src="//js.rtoaster.jp/${environments.RTOASTER_CODE}/rt.js",(s=r.getElementsByTagName(t)[0]).parentNode.insertBefore(j,s)}(document,"script")`}</script>
        <link rel="canonical" href={`${STATIC_DPP_TOP_URL}${BASENAME}${pathname}`} />
        <script charset="UTF-8" type="text/javascript" src={stageAerosmithJS}></script>
        <script>window.addEventListener('load', () => aerosmith.complete())</script>
      </Helmet>
    );
  }

  default: {
    const headerJS = loggedInHeaderJS;
    const footerJS = defaultFooterJS;
    return (
      <Helmet>
        <script type="text/javascript" src={headerJS} charset="utf-8"></script>
        <script type="text/javascript" src={footerJS} charset="utf-8"></script>
        <script type="text/javascript">{`!function(r,t,j,s){(j=r.createElement(t)).type="text/javascript",j.async=!0,j.charset="utf-8",j.src="//js.rtoaster.jp/${environments.RTOASTER_CODE}/rt.js",(s=r.getElementsByTagName(t)[0]).parentNode.insertBefore(j,s)}(document,"script")`}</script>
        <link rel="canonical" href={`${STATIC_DPP_TOP_URL}${BASENAME}${pathname}`} />
      </Helmet>
    );
  }
  }
};

export default Head;

export const FData = ({ action, flightState, plnaDetailState }) => {
  const { plans } = plnaDetailState;
  const plan = plans && plans.length && plans[0].plan;
  const isReady = !!plan;
  if (!isReady) {
    return null;
  }

  const f_data = makeFData(action, flightState, plnaDetailState);

  return (
    <Helmet>
      <script type="text/javascript">{`
        var _f_data = ${JSON.stringify(f_data)};
      `}</script>
    </Helmet>
  );
};

FData.propTypes = {
  action: PropTypes.string.isRequired,
  flightState: PropTypes.object.isRequired,
  plnaDetailState: PropTypes.object.isRequired,
};

const makeFData = (action, flightState, plnaDetailState) => {
  const isPlanDetail = action === 'detail';
  const list = isPlanDetail ? 'プラン詳細' : undefined;
  const step = isPlanDetail ? undefined : '1';
  const number = isPlanDetail ? '1' : undefined;
  const { plans } = plnaDetailState;
  const plan = plans && plans.length && plans[0].plan;
  const { id: planID, roomType, hotel } = plan;
  const hotelID = isPlanDetail ? undefined : hotel.id;
  const roomTypeID = roomType.id;
  const { directionName: regionName, prefectureName, areaName } = hotel.area;
  const {
    selectedOutboundFlight,
    selectedInboundFlight,
    selectedOutboundDate,
    selectedInboundDate,
    searchedFlightsInput,
  } = flightState;
  const { departureAirportCode, airline } = selectedOutboundFlight;
  const productsId = `${planID}-${roomTypeID}-${departureAirportCode}`;
  const brand = airline.toUpperCase();
  const tripStartDate = moment(selectedOutboundDate);
  const tripEndDate = moment(selectedInboundDate);
  const leadtime = tripEndDate.diff(tripStartDate, 'days');
  const leadtimeDisplay = isPlanDetail ? undefined : leadtime.toString();
  const tripStartDateDisplay = tripStartDate.format('YYYYMMDD');
  const tripEndDateDisplay = tripEndDate.format('YYYYMMDD');
  const { people } = searchedFlightsInput;
  const adult = people.adult || 2;
  const childrenA = people.childrenA || 0;
  const childrenB = people.childrenB || 0;
  const peopleNumber = adult + childrenA + childrenB;
  const quantity = isPlanDetail ? undefined : peopleNumber.toString();
  const { totalPrice, pricePerAdult } = usePrice(
    people, selectedOutboundFlight, selectedInboundFlight, plans.map(p => p.plan), selectedOutboundDate
  );
  const searchConditionInfoParams = isPlanDetail ? plan.searchConditionInfo.map(p => p.param) : [];
  const searchcondition = searchConditionInfoParams.join(',');

  return {
    action,
    list,
    step,
    productsID: productsId,
    brand,
    category_1: '航空機セットプラン',
    startdateID: tripStartDateDisplay,
    enddateID: tripEndDateDisplay,
    category_3: regionName,
    category_4: prefectureName,
    category_5: areaName,
    variant: departureAirportCode,
    price: pricePerAdult.toString(),
    priceID: totalPrice.toString(),
    quantity,
    number,
    hotelID,
    leadtime: leadtimeDisplay,
    // customerID: userID,
    searchcondition
  };
};

export const FDataFromSession = ({ session, step }) => {
  const f_data = makeFDataFromSession(session, step);

  return (
    <Helmet>
      <script type="text/javascript">{`
        var _f_data = ${JSON.stringify(f_data)};
      `}</script>
    </Helmet>
  );
};

FDataFromSession.propTypes = {
  session: PropTypes.object.isRequired,
  step: PropTypes.number.isRequired,
};

const makeFDataFromSession = (session, step) => {
  const { flightSchedule, plans, member } = session;
  const plan = plans[0];
  const { planID, roomTypeID } = plan;
  const { departure } = flightSchedule;
  const { departureAirportCode } = departure;
  const productsId = `${planID}-${roomTypeID}-${departureAirportCode}`;
  const userID = member ? member.userID : undefined;

  return {
    action: 'checkout_option',
    step: step.toString(),
    productsID: productsId,
    category_1: '航空機セットプラン',
    customerID: userID,
  };
};

export const RtoasterOnComplete = ({ session, reservationId }) => {
  const pcView = window.innerWidth > 768;
  const rtoasterLabel1 = pcView ? 'rt_pc_kokunai_complete_01' : 'rt_sp_kokunai_complete_01';
  const rtoasterLabel2 = pcView ? 'rt_pc_kokunai_complete_02' : 'rt_sp_kokunai_complete_02';

  const f_data = makeFDataFromSessionComplete(session, reservationId);

  return (
    <Helmet>
      <script type="text/javascript">{`
        var _f_data = ${JSON.stringify(f_data)};
        Rtoaster.init("${environments.RTOASTER_CODE}");
        Rtoaster.track(_f_data);
        Rtoaster.recommendNow("${rtoasterLabel1}", "${rtoasterLabel2}");
        dataLayer.push({'event': 'priceID_complete'});
      `}</script>
    </Helmet>
  );
};

RtoasterOnComplete.propTypes = {
  session: PropTypes.object.isRequired,
  reservationId: PropTypes.string.isRequired,
};

const makeFDataFromSessionComplete = (session, reservationId) => {
  const {
    flightSchedule, plans, totalAmount, member, accompanies,
  } = session;
  const plan = plans[0];
  const { planID, roomTypeID, regionName, prefectureName, areaName, adultPrice } = plan;
  const { departure, arrival, airline } = flightSchedule;
  const { departureAirportCode, price: departurePrice } = departure;
  const { price: arrivalPrice } = arrival;
  const productsId = `${planID}-${roomTypeID}-${departureAirportCode}`;
  const brand = airline.toUpperCase();
  const tripStartDayString = departure.departureTime.substr(0, 10);
  const tripStartDate = moment(tripStartDayString).format('YYYYMMDD');
  const tripEndDayString = arrival.arrivalTime.substr(0, 10);
  const tripEndDate = moment(tripEndDayString).format('YYYYMMDD');

  const accompanyCount = accompanies ? accompanies.length : 0;
  const peopleNumber = accompanyCount + 1;
  const priceIndividual = departurePrice + arrivalPrice + adultPrice;

  const { userID, sex, birthday } = member;
  const age = getAgeFromBirthday(birthday);
  const sexId = sex === 'MALE' ? '1' : '2';

  return {
    action: 'purchase',
    productsID: productsId,
    reservationID: reservationId,
    tax: '0',
    shipping: '0',
    brand,
    category_1: '航空機セットプラン',
    startdateID: tripStartDate,
    enddateID: tripEndDate,
    category_3: regionName,
    category_4: prefectureName,
    category_5: areaName,
    variant: departureAirportCode,
    price: priceIndividual.toString(),
    priceID: totalAmount.toString(),
    quantity: peopleNumber.toString(),
    number: '1',
    customerID: userID,
    ageID: age.toString(),
    sexID: sexId,
  };
};